// "Por tienda": lo pagado por lo que se vendió en cada tienda, sin bonos por umbral ni renta.
// Mockup: surekeep/mockups/backoffice/portal-admin-vl-com-pagadas.html
import { useState } from 'react';
import { formatMoneyCents, formatShortMXN } from '@/shared/lib/format/money';
import { CellLines, DataTable, type DataTableColumn } from '@/shared/patterns/DataTable';
import { MasterDetailEmpty, MasterDetailExplorer } from '@/shared/patterns/MasterDetail';
import { ScreenErrorBoundary } from '@/shared/patterns/ScreenErrorBoundary';
import { ToolbarMetric } from '@/shared/patterns/TableToolbar';
import { usePayoutRows } from '../../api/usePayoutRows';
import {
    activityCounts,
    applyActivity,
    buildStoreRows,
    contributorsNote,
    filterStoreRows,
    nextPaidSort,
    sellersAtLeastTitle,
    sellersText,
    sortRows,
    STORE_SORT,
    type ActivityPill,
    type PaidSort,
    type StorePaidRow,
    type StoreSortKey,
} from '../../lib/paidRows';
import { DEFAULT_PAYOUT_PERIOD, periodText } from '../../lib/payoutPeriods';
import { FIELD, LABEL } from '../fieldStyles';
import {
    PaidDetailUnavailable,
    PaidFilters,
    PaidListFrame,
    type PaidTabProps,
} from './PaidListFrame';
import { PayoutPeriodSelect } from './PayoutPeriodSelect';
import { ShareCell } from './ShareCell';
import { StorePaidDetail } from './StorePaidDetail';

const sold = (row: StorePaidRow) => row.salesCount > 0;

export function StoresPaidTab({ period, onPeriodChange, today, directory }: PaidTabProps) {
    const source = usePayoutRows('STORE', period, today);
    const [search, setSearch] = useState('');
    const [pill, setPill] = useState<ActivityPill>('all');
    const [sort, setSort] = useState<PaidSort<StoreSortKey>>({ key: 'paid', direction: 'desc' });
    const [selected, setSelected] = useState<string | null>(null);

    const all = buildStoreRows(
        source.rows,
        directory.stores,
        directory.storeZone,
        source.sellersAtLeast,
    );
    const filtered = filterStoreRows(all, search);
    const counts = activityCounts(filtered, sold);
    const rows = sortRows(applyActivity(filtered, pill, sold), sort, STORE_SORT);
    const maxCents = Math.max(0, ...rows.map((row) => row.paidCents));
    const when = periodText(period);
    // Sin tienda elegida se abre la primera cuando las fuentes ya respondieron, y queda fijada.
    const firstId = rows.at(0)?.storeId ?? null;
    if (selected === null && source.isReady && firstId !== null) setSelected(firstId);
    const selectedRow =
        selected === null ? undefined : (all.find((row) => row.storeId === selected) ?? rows.at(0));

    const columns: DataTableColumn<StorePaidRow, StoreSortKey>[] = [
        {
            key: 'name',
            label: 'Tienda',
            sortKey: 'name',
            cell: (row) => (
                <CellLines
                    primary={row.name}
                    secondary={[row.code, row.zone ?? ''].filter((part) => part !== '').join(' · ')}
                />
            ),
        },
        {
            key: 'sellers',
            label: 'Vendedores',
            width: '84px',
            numeric: true,
            sortKey: 'sellers',
            cell: (row) => sellersText(row),
            cellTitle: sellersAtLeastTitle,
        },
        {
            key: 'sales',
            label: 'Ventas',
            width: '70px',
            numeric: true,
            sortKey: 'sales',
            note: when,
            cell: (row) => row.salesCount,
        },
        {
            key: 'placement',
            label: 'Colocación',
            width: '96px',
            numeric: true,
            sortKey: 'placement',
            note: when,
            cell: (row) => formatShortMXN(row.placementCents / 100),
        },
        {
            key: 'paid',
            label: 'Comisiones · % del total',
            width: '230px',
            sortKey: 'paid',
            note: 'sin umbrales ni renta',
            cell: (row) => (
                <ShareCell
                    paidCents={row.paidCents}
                    totalCents={source.totalPaidCents}
                    maxCents={maxCents}
                />
            ),
        },
    ];

    const filters = (
        <PaidFilters
            onClear={() => {
                setSearch('');
                setPill('all');
                onPeriodChange(DEFAULT_PAYOUT_PERIOD);
            }}
            note="Por tienda cuenta la comisión por venta: sin bonos por umbral ni renta de cartera, que son del vendedor. El total no cuadra con las otras pestañas."
        >
            <div>
                <label htmlFor="stores-paid-search" className={LABEL}>
                    Búsqueda
                </label>
                <input
                    id="stores-paid-search"
                    className={FIELD}
                    placeholder="Tienda, clave o zona"
                    value={search}
                    onChange={(event) => {
                        setSearch(event.target.value);
                    }}
                />
            </div>
            <PayoutPeriodSelect period={period} onChange={onPeriodChange} today={today} />
        </PaidFilters>
    );

    const list = (
        <PaidListFrame
            pills={[
                { key: 'all', label: 'Todas', value: counts.all },
                { key: 'with', label: 'Con ventas', value: counts.with, tone: 'success' },
                { key: 'without', label: 'Sin ventas', value: counts.without },
            ]}
            pill={pill}
            onPill={setPill}
            pillsLabel="Tiendas con y sin ventas"
            metrics={
                <>
                    <ToolbarMetric label="Tiendas" value={source.error ? '—' : rows.length} />
                    <ToolbarMetric
                        label={`Comisiones · ${when}`}
                        value={source.error ? '—' : formatMoneyCents(source.totalPaidCents)}
                    />
                </>
            }
            error={source.error}
            errorTitle="No se pudo cargar lo liquidado por tienda"
            onRetry={source.refetch}
        >
            <DataTable
                columns={columns}
                rows={rows}
                rowKey={(row) => row.storeId}
                caption="Comisiones por tienda"
                sort={sort}
                onSortToggle={(key) => {
                    setSort(nextPaidSort(sort, key, ['name']));
                }}
                isRowSelected={(row) => row.storeId === selectedRow?.storeId}
                onRowClick={(row) => {
                    setSelected(row.storeId);
                }}
                isLoading={source.isLoading}
                empty="Ninguna tienda coincide con los filtros."
                density="compact"
            />
        </PaidListFrame>
    );

    const detail = source.error ? (
        <PaidDetailUnavailable />
    ) : selectedRow ? (
        <ScreenErrorBoundary
            title="No se pudo mostrar el detalle de la tienda"
            resetKeys={[selectedRow.storeId]}
        >
            <StorePaidDetail
                key={selectedRow.storeId}
                row={selectedRow}
                totalCents={source.totalPaidCents}
                when={when}
                directory={directory}
                contributorsNote={contributorsNote(source)}
            />
        </ScreenErrorBoundary>
    ) : (
        <MasterDetailEmpty
            title="Selecciona una tienda"
            sub="Elige una fila para ver su costo por semana y sus vendedores."
        />
    );

    return (
        <MasterDetailExplorer
            filters={filters}
            list={list}
            detail={detail}
            storageKey="comisiones-pagadas-tienda"
        />
    );
}
